import React, {FormEvent, ReactNode, useEffect, useState} from 'react';
import {Button, Col, FormControl, Row} from 'react-bootstrap';
import Navbar from '../navigation/navbar';
import LeftSide from '../main/left-side';
import RightSide from '../main/right-side';
import TitleCard from '../main/title-card';
import LargeCard from '../main/large-card';
import {ActivateManagerAndSetPassword, GetManagerActivationInformation,} from '../../store/loginManagerActivationSlice';
import {RequestStatus} from '../../store/network/managedRequest';
import ValidatorFormGroup from '../main/validator-form-group';
import {LoginAsPartner} from '../../store/authSlice';
import Spinner from '../util/spinner';
import useTranslate from '../useTranslate';
import {WEBSITE} from '../../constants';
import {usePbDispatch, usePbSelector} from '../../store/pbStoreHooks';
import {PbRequest} from '../../store/pbRequest';
import {goToEndOfLogin} from '../../nav/navigationService';
import {useParams} from "react-router-dom";

const ACTIVATION_IN_PROGRESS = true;
const ACTIVATION_DONE = false;

const TAG_PASSWORD_INSECURE = 'PASSWORD_INSECURE';

interface FormValues {
    password: string;
    repeatPassword: string;
    tacAccepted: boolean;
}

const VALIDATORS = {
    password: {
        isValid(val: FormValues) {
            return (
                !!val.password &&
                /^(?=\D*\d)(?=.*?[a-zA-Z]).*[\W_].*$/.test(val.password) && // NOSONAR(javascript:S5852)
                val.password.length > 7
            );
        },
    },
    repeatPassword: {
        isValid(val: FormValues) {
            return !!val.repeatPassword && val.repeatPassword === val.password;
        },
    },
    tacAccepted: {
        isValid(val: FormValues) {
            return val.tacAccepted;
        },
    },
};

function LoginActivationScreen() {
    const strings = useTranslate('LoginActivationScreen');
    const params = useParams();
    const token = params?.key;
    const dispatch = usePbDispatch();
    const getInformationData = usePbSelector(
        (state) => state.loginManagerActivation.information
    );
    const getInformation = usePbSelector(
        (state) => state.pbRequest[PbRequest.GET_MANAGER_ACVIVATION_INFORMATION]
    );
    const postActivateAndSetPassword = usePbSelector(
        (state) =>
            state.pbRequest[PbRequest.POST_MANAGER_ACTIVATION_AND_SET_PASSWORD]
    );
    const [formValues, setFormValues] = useState<FormValues>({
        password: '',
        repeatPassword: '',
        tacAccepted: false,
    });
    const [dirtyValues, setDirtyValues] = useState<Partial<FormValues>>({});
    const [activationInProgress, setActivationInProgress] = useState(false);

    const tacUrl = getWebsiteUrl(strings.tacUrlRoute);
    const ppUrl = getWebsiteUrl(strings.ppUrlRoute);

    useEffect(() => {
        if (!token) {
            return;
        }
        dispatch(GetManagerActivationInformation(token));
    }, [token]);

    return (
        <Navbar nav={false}>
            <Row>
                <LeftSide>
                    <TitleCard title={strings.title} />
                </LeftSide>
                <Col xs={6} className={'col-middle'}>
                    <LargeCard title={strings.title} type={'card-edit'}>
                        {renderCardContent()}
                    </LargeCard>
                </Col>
                <RightSide />
            </Row>
        </Navbar>
    );

    function renderCardContent(): ReactNode {
        if (
            [RequestStatus.INITIAL, RequestStatus.PENDING].includes(
                getInformation.status
            )
        ) {
            return renderCardContentLoading();
        } else if (RequestStatus.SUCCESS === getInformation.status) {
            return renderCardContentNormal();
        } else {
            return renderCardContentExpired();
        }
    }

    function renderCardContentLoading(): ReactNode {
        return <div>{strings.textLoading}</div>;
    }

    function renderCardContentExpired(): ReactNode {
        return <div>{strings.textExpired}</div>;
    }

    function renderCardContentNormal(): ReactNode {
        return (
            <form onSubmit={handleSubmit}>
                <div className={'mb-5'}>
                    {strings._utils.sprintf(
                        strings.textInfo,
                        getInformationData?.email
                    )}
                </div>
                <ValidatorFormGroup
                    label={strings.fieldPassword}
                    hideErrorLabel={
                        dirtyValues.password === undefined ||
                        VALIDATORS.password.isValid(formValues)
                    }
                    errorLabel={strings.fieldPasswordError}
                    helpLabel={strings.hintPassword}
                >
                    <FormControl
                        type={'password'}
                        className={'input-text-color'}
                        value={formValues.password}
                        onChange={(event: FormEvent<FormControl>) =>
                            persistFormValue({
                                password: (event.target as HTMLInputElement)
                                    .value,
                            })
                        }
                        required
                    />
                </ValidatorFormGroup>
                <ValidatorFormGroup
                    label={strings.fieldRepeatPassword}
                    hideErrorLabel={
                        dirtyValues.repeatPassword === undefined ||
                        VALIDATORS.repeatPassword.isValid(formValues)
                    }
                    errorLabel={strings.fieldRepeatPasswordError}
                >
                    <FormControl
                        type={'password'}
                        className={'input-text-color'}
                        value={formValues.repeatPassword}
                        onChange={(event: FormEvent<FormControl>) =>
                            persistFormValue({
                                repeatPassword: (
                                    event.target as HTMLInputElement
                                ).value,
                            })
                        }
                        required
                    />
                </ValidatorFormGroup>
                <ValidatorFormGroup
                    label={''}
                    errorLabel={strings.accept_tacError}
                    hideErrorLabel={
                        dirtyValues.tacAccepted === undefined ||
                        VALIDATORS.tacAccepted.isValid(formValues)
                    }
                >
                    <div className="checkbox checkbox-primary checkbox-custom">
                        <input
                            type="checkbox"
                            id="terms_accepted"
                            className="styled"
                            checked={formValues.tacAccepted}
                            onChange={(event) =>
                                persistFormValue({
                                    tacAccepted: event.target.checked,
                                })
                            }
                        />
                        <label htmlFor="terms_accepted">
                            {strings.termsAndCond1}
                            <a className="icon" href={tacUrl} target="_blank">
                                {strings.tac}
                            </a>
                            {strings.and}
                            <a className="icon" href={ppUrl} target="_blank">
                                {strings.pp}
                            </a>
                            {strings.termsAndCond2}
                        </label>
                    </div>
                </ValidatorFormGroup>
                <div className={'form-footer'}>
                    <Row>
                        <Col xs={6}>
                            <Button
                                className={'btn-primary btn btn-default'}
                                type={'submit'}
                                disabled={!isValid() || activationInProgress}
                            >
                                {renderButtonContent()}
                            </Button>
                            {maybeRenderError()}
                        </Col>
                    </Row>
                </div>
            </form>
        );
    }

    function renderButtonContent() {
        if (activationInProgress) {
            return (
                <>
                    <Spinner /> {strings.submitInProgress}
                </>
            );
        } else {
            return strings.submit;
        }
    }

    function maybeRenderError(): ReactNode {
        if (RequestStatus.FAILURE !== postActivateAndSetPassword.status) {
            return null;
        }

        let errorText: string = strings.genericError;
        if (postActivateAndSetPassword.errors.includes(TAG_PASSWORD_INSECURE)) {
            errorText = strings.fieldPasswordError;
        }

        return (
            <div className="alert alert-danger pull-right">
                <i className="fa fa-exclamation-circle fa-fw" /> {errorText}
            </div>
        );
    }

    function persistFormValue(changeSet: Partial<FormValues>) {
        setFormValues({ ...formValues, ...changeSet });
        setDirtyValues({ ...dirtyValues, ...changeSet });
    }

    function handleSubmit(event: FormEvent) {
        event.preventDefault();
        if (!isValid()) {
            return;
        }
        activateAndSetPassword(formValues.password, formValues.tacAccepted);
    }

    function isValid() {
        let valid = true;
        Object.keys(VALIDATORS).forEach(
            (key) => (valid = valid && VALIDATORS[key].isValid(formValues))
        );
        return valid;
    }

    function activateAndSetPassword(password: string, tac_accepted: boolean) {
        setActivationInProgress(ACTIVATION_IN_PROGRESS);
        const email = getInformationData?.email;
        dispatch(
            ActivateManagerAndSetPassword(
                { token, password, tac_accepted },
                handleActivateResult
            )
        );

        function handleActivateResult(success: boolean) {
            if (success) {
                dispatch(LoginAsPartner(email, password, goToEndOfLogin));
            } else {
                setActivationInProgress(ACTIVATION_DONE);
            }
        }
    }
}

function getWebsiteUrl(urlPart: string) {
    return `${WEBSITE}${urlPart}`;
}

export default LoginActivationScreen;
